'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { Icon } from '@/ui/icons/icon/Icon'
import { HighlightedText } from '@/ui/text/highlighted-text/HighlightedText.client'
import { useContext } from 'react'
import { AutocompleteStateContext, ListBoxItem } from 'react-aria-components'
import { SELECT_OPTION_CLASSNAME } from '../select.styles'

const selectCheckboxCva = twMerge(
  'flex size-2 shrink-0 items-center justify-center rounded-xs border-px border-solid border-brand-2-400 bg-white hd:border-1.5',
  'group-data-[selected]:border-brand-2-800 group-data-[selected]:bg-brand-2-800',
  'group-data-[disabled]:border-brand-2-400 group-data-[disabled]:bg-white',
  'group-data-[disabled]:group-data-[selected]:border-brand-2-400 group-data-[disabled]:group-data-[selected]:bg-brand-2-400',
)

export type SelectListItemData = {
  id: string | number
  label: string
  additionalText?: string
}

type SelectListItemProps = {
  item: SelectListItemData
}

function SelectItemLabel({
  label,
  isSelected,
  isDisabled,
}: {
  label: string
  isSelected: boolean
  isDisabled: boolean
}) {
  const autocompleteState = useContext(AutocompleteStateContext)
  const highlight = autocompleteState?.inputValue ?? ''
  const labelClassName = twMerge(
    'truncate text-md leading-light',
    isDisabled ? 'text-brand-2-400' : isSelected ? 'font-bold text-brand-1-800' : 'font-medium',
  )

  if (!highlight) {
    return <span className={labelClassName}>{label}</span>
  }

  return (
    <HighlightedText
      highlight={highlight}
      caseSensitive={false}
      accentSensitive={false}
      color="primary"
      className={twMerge('m-0', labelClassName)}
    >
      {label}
    </HighlightedText>
  )
}

export function SelectListItem({ item }: SelectListItemProps) {
  return (
    <ListBoxItem
      id={item.id}
      textValue={item.label}
      className={twMerge(
        'group min-h-6 data-[hovered]:cursor-pointer data-[hovered]:bg-brand-2-50 data-[selected]:bg-brand-1-50',
        'data-[disabled]:data-[hovered]:bg-transparent data-[disabled]:cursor-not-allowed',
        SELECT_OPTION_CLASSNAME,
        item.additionalText && 'items-start py-1',
      )}
    >
      {({ selectionMode, isSelected, isDisabled }) => (
        <>
          {selectionMode === 'multiple' && (
            <span aria-hidden className="flex min-h-[1.225rem] shrink-0 items-center">
              <span className={selectCheckboxCva}>
                <Icon.Check size="sm" className="hidden text-white group-data-[selected]:block" />
              </span>
            </span>
          )}
          <div
            className={twMerge(
              'flex min-w-0 flex-1 flex-col',
              !item.additionalText && 'justify-center',
            )}
          >
            <SelectItemLabel label={item.label} isSelected={isSelected} isDisabled={isDisabled} />
            {item.additionalText && (
              <span
                className={twMerge(
                  'truncate text-xs font-normal leading-lightest',
                  isDisabled ? 'text-brand-2-400' : 'text-brand-2-900',
                )}
              >
                {item.additionalText}
              </span>
            )}
          </div>
          {selectionMode === 'single' && isSelected && (
            <span
              className={twMerge(
                'shrink-0',
                item.additionalText && 'flex min-h-[1.225rem] items-center',
              )}
            >
              <Icon.Check size="sm" className="text-brand-1-700" aria-hidden />
            </span>
          )}
        </>
      )}
    </ListBoxItem>
  )
}
